'use client';

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { mutate } from 'swr';
import { Bot, Sparkles, Smile, X, Check, Clock, UserCheck, Moon, Sun } from 'lucide-react';

interface StatusModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user?: any;
}

const PRESET_EMOJIS = ['💬', '☕', '🥪', '📅', '🎯', '🌴', '🤒', '🚀', '💻', '📞', '🏠', '🍔', '✈️', '🎧', '⚡'];

const STATUS_PRESETS = [
  { emoji: '💬', text: 'Active', ai: false, clearAfter: 'never', presence: 'active' as const },
  { emoji: '☕', text: 'On Break', ai: true, clearAfter: '15m', presence: 'away' as const },
  { emoji: '🥪', text: 'Out to lunch', ai: true, clearAfter: '30m', presence: 'away' as const },
  { emoji: '📅', text: 'In a meeting', ai: true, clearAfter: '1h', presence: 'away' as const },
  { emoji: '🎯', text: 'Focusing', ai: false, clearAfter: '2h', presence: 'active' as const },
  { emoji: '🌴', text: 'On vacation', ai: true, clearAfter: 'today', presence: 'away' as const },
  { emoji: '🤒', text: 'Out sick', ai: true, clearAfter: 'today', presence: 'away' as const },
  { emoji: '🏠', text: 'Working remotely', ai: false, clearAfter: 'today', presence: 'active' as const },
];

export function StatusModal({ open, onOpenChange, user }: StatusModalProps) {
  const [presenceStatus, setPresenceStatus] = useState<'active' | 'away'>('active');
  const [emoji, setEmoji] = useState('💬');
  const [statusText, setStatusText] = useState('');
  const [aiHandleChats, setAiHandleChats] = useState(false);
  const [clearAfter, setClearAfter] = useState<string>('never');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open && user) {
      setPresenceStatus(user.presenceStatus === 'away' ? 'away' : 'active');
      setEmoji(user.customStatusEmoji || '💬');
      setStatusText(user.customStatusText || '');
      setAiHandleChats(!!user.aiHandleChats);
      setClearAfter('never');
      setShowEmojiPicker(false);
    }
  }, [open, user]);

  const handleApplyPreset = (preset: typeof STATUS_PRESETS[0]) => {
    setEmoji(preset.emoji);
    setStatusText(preset.text);
    setAiHandleChats(preset.ai);
    setClearAfter(preset.clearAfter);
    setPresenceStatus(preset.presence);
    setShowEmojiPicker(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      let clearAfterMinutes: number | 'today' | null = null;
      if (clearAfter === '15m') clearAfterMinutes = 15;
      else if (clearAfter === '30m') clearAfterMinutes = 30;
      else if (clearAfter === '1h') clearAfterMinutes = 60;
      else if (clearAfter === '2h') clearAfterMinutes = 120;
      else if (clearAfter === '4h') clearAfterMinutes = 240;
      else if (clearAfter === 'today') clearAfterMinutes = 'today';

      const payload = {
        presenceStatus,
        customStatusEmoji: statusText.trim() ? emoji : null,
        customStatusText: statusText.trim() ? statusText.trim() : null,
        aiHandleChats,
        clearAfterMinutes,
      };

      const res = await fetch('/api/user/presence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error('Failed to update status');
      }

      await mutate('/api/user');
      await mutate('/api/user/presence');
      await mutate('/api/team/members');

      toast.success(
        presenceStatus === 'away' || aiHandleChats
          ? 'Status saved. AI agent will handle your assigned chats.'
          : 'Status saved. You will handle your assigned chats.'
      );
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update status');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearStatus = async () => {
    setIsSaving(true);
    try {
      const res = await fetch('/api/user/presence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clearStatus: true,
          presenceStatus: 'active',
        }),
      });

      if (!res.ok) throw new Error('Failed to clear status');

      await mutate('/api/user');
      await mutate('/api/user/presence');
      await mutate('/api/team/members');

      toast.success('Status cleared. You are now Active.');
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to clear status');
    } finally {
      setIsSaving(false);
    }
  };

  const hasCustomStatus = Boolean(user?.customStatusText || user?.customStatusEmoji || user?.aiHandleChats || user?.presenceStatus === 'away');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] p-6 rounded-2xl bg-card border shadow-2xl">
        <DialogHeader className="space-y-1">
          <DialogTitle className="text-lg font-bold text-foreground flex items-center justify-between">
            <span>Set a status</span>
            <span className="text-xs font-normal text-muted-foreground flex items-center gap-1.5 bg-muted/60 px-2.5 py-1 rounded-full">
              {presenceStatus === 'active' ? (
                <>
                  <span className="size-2 rounded-full bg-emerald-500 animate-pulse" />
                  Active
                </>
              ) : (
                <>
                  <span className="size-2 rounded-full bg-amber-500" />
                  Away
                </>
              )}
            </span>
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Let your team know what you&apos;re doing and let AI handle chats while away.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Quick Presence Toggle */}
          <div className="flex items-center gap-2 p-1.5 rounded-xl bg-muted/40 border border-border/50">
            <button
              type="button"
              onClick={() => {
                setPresenceStatus('active');
                if (statusText === 'Away') setStatusText('');
              }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
                presenceStatus === 'active'
                  ? 'bg-background text-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className="size-2.5 rounded-full bg-emerald-500 shrink-0" />
              <span>Set as Active</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setPresenceStatus('away');
                setAiHandleChats(true);
              }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
                presenceStatus === 'away'
                  ? 'bg-background text-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className="size-2.5 rounded-full border-2 border-amber-500 bg-transparent shrink-0" />
              <span>Set as Away</span>
            </button>
          </div>

          {/* Custom Status Input with Emoji Trigger */}
          <div className="space-y-1.5">
            <div className="relative flex items-center rounded-xl border border-input bg-background focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary transition-all">
              <button
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="size-10 flex items-center justify-center text-lg hover:bg-muted/60 rounded-l-xl transition-colors shrink-0 outline-none"
                title="Choose emoji"
              >
                {emoji || <Smile className="size-5 text-muted-foreground" />}
              </button>
              <Input
                value={statusText}
                onChange={(e) => setStatusText(e.target.value)}
                placeholder="What's your status? (e.g. On Break, Lunch, Meeting)"
                className="border-0 shadow-none focus-visible:ring-0 text-sm px-2 h-10 flex-1 bg-transparent"
                maxLength={100}
              />
              {statusText && (
                <button
                  type="button"
                  onClick={() => setStatusText('')}
                  className="size-8 flex items-center justify-center text-muted-foreground hover:text-foreground mr-1 rounded-full"
                >
                  <X className="size-3.5" />
                </button>
              )}
            </div>

            {/* Quick Emoji Picker Drawer */}
            {showEmojiPicker && (
              <div className="p-2.5 rounded-xl bg-muted/60 border border-border/60 animate-in fade-in slide-in-from-top-1 duration-150">
                <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-2">
                  Choose an emoji
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {PRESET_EMOJIS.map((e) => (
                    <button
                      key={e}
                      type="button"
                      onClick={() => {
                        setEmoji(e);
                        setShowEmojiPicker(false);
                      }}
                      className="size-8 rounded-lg flex items-center justify-center text-base hover:bg-background transition-transform active:scale-95"
                    >
                      {e}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Quick Presets Pills */}
          <div className="space-y-1.5">
            <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
              Suggestions
            </div>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_PRESETS.map((preset) => (
                <button
                  key={preset.text}
                  type="button"
                  onClick={() => handleApplyPreset(preset)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                    statusText === preset.text
                      ? 'bg-primary/10 text-primary border-primary/30 font-semibold'
                      : 'bg-muted/40 text-foreground border-border/50 hover:bg-muted'
                  }`}
                >
                  <span>{preset.emoji}</span>
                  <span>{preset.text}</span>
                </button>
              ))}
            </div>
          </div>

          {/* AI CHAT TAKEOVER CHECKBOX */}
          <div className="p-3.5 rounded-xl bg-emerald-500/5 dark:bg-emerald-950/20 border border-emerald-500/20 space-y-1">
            <div className="flex items-start gap-3">
              <Checkbox
                id="aiHandleChats"
                checked={aiHandleChats}
                onCheckedChange={(checked) => setAiHandleChats(!!checked)}
                className="mt-0.5 border-emerald-500 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
              />
              <div className="space-y-0.5">
                <Label
                  htmlFor="aiHandleChats"
                  className="text-xs font-bold text-foreground flex items-center gap-1.5 cursor-pointer"
                >
                  <Bot className="size-3.5 text-emerald-600 dark:text-emerald-400" />
                  <span>Handle chat by AI</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 font-semibold">
                    Auto-Reply
                  </span>
                </Label>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  When enabled, AI agent will instantly answer all incoming messages from your assigned customers while you are away or on break.
                </p>
              </div>
            </div>
          </div>

          {/* Clear After Dropdown */}
          <div className="space-y-1.5">
            <Label className="text-xs font-bold text-foreground flex items-center gap-1.5">
              <Clock className="size-3.5 text-muted-foreground" />
              <span>Clear status after</span>
            </Label>
            <Select value={clearAfter} onValueChange={setClearAfter}>
              <SelectTrigger className="w-full text-xs h-9 rounded-xl">
                <SelectValue placeholder="Select duration" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="never">Don&apos;t clear</SelectItem>
                <SelectItem value="15m">15 minutes</SelectItem>
                <SelectItem value="30m">30 minutes</SelectItem>
                <SelectItem value="1h">1 hour</SelectItem>
                <SelectItem value="2h">2 hours</SelectItem>
                <SelectItem value="4h">4 hours</SelectItem>
                <SelectItem value="today">Today (until midnight)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground">
              When expired, your status clears automatically and normal customer routing resumes.
            </p>
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between gap-2 pt-2 border-t border-border/50">
          <div>
            {hasCustomStatus && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClearStatus}
                disabled={isSaving}
                className="text-xs text-destructive hover:bg-destructive/10 rounded-xl"
              >
                Clear status
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
              className="text-xs rounded-xl"
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={isSaving}
              className="text-xs rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
            >
              {isSaving ? 'Saving...' : 'Save Status'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default StatusModal;
